/**
 * useVendorTermsLookup
 *
 * Fetches all vendor_terms rows for the authenticated company and exposes a
 * lookup function keyed by vendor name (case-insensitive, matching on
 * supplier_name).  Used by the apply-agreed-terms nudge in OptimizeTab.
 */

import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { VendorTerms } from "@/types/negotiations";

async function fetchVendorTerms(): Promise<VendorTerms[]> {
  const { data, error } = await supabase.from("vendor_terms").select("*");
  if (error) throw error;
  return (data ?? []) as VendorTerms[];
}

export interface UseVendorTermsLookupResult {
  /** Returns the agreed VendorTerms row for a vendor name, or null if none. */
  getTermsForVendor: (vendorName: string) => VendorTerms | null;
  isLoading: boolean;
}

export function useVendorTermsLookup(): UseVendorTermsLookupResult {
  const { data = [], isLoading } = useQuery<VendorTerms[]>({
    queryKey: ["vendor_terms_all"],
    queryFn: fetchVendorTerms,
    staleTime: 60_000,
  });

  const termsByName = new Map<string, VendorTerms>(
    data.map((vt) => [(vt.supplier_name ?? "").toLowerCase(), vt])
  );

  const getTermsForVendor = (vendorName: string): VendorTerms | null =>
    termsByName.get(vendorName.toLowerCase()) ?? null;

  return { getTermsForVendor, isLoading };
}
